'use client';

import * as Dialog from '@radix-ui/react-dialog';
import { ampli } from '@/ampli';
import Image from 'next/image';
import { useRef, useState } from 'react';
import { useMedia } from 'react-use';
import { toast } from 'sonner';

import { logger } from '@/lib/logger';
import type { PaidFeedbackType } from '@/lib/paidFeedback/types';

import { PaidFeedbackToast } from '@/components/TransactionModule/TransactionToast/PaidFeedbackToast';

import { USDC_ADDRESSES } from '@/constants/addresses';
import { CHAIN_IDS } from '@/constants/chain';

import { useMatchaWallets } from '@/hooks/useMatchaWallets';

import { buttons } from '@/styles/buttons';
import { inputStyles } from '@/styles/input';
import { fontStyles } from '@/styles/typography';
import { breakpoints } from '@/styles/util';

import { TokenAvatar } from '@/ui/Avatars/TokenAvatar';
import { ShieldTickIcon } from '@/ui/Icons/ShieldTickIcon';
import { WarningCircleSolidIcon } from '@/ui/Icons/WarningCircleSolidIcon';
import { XIcon } from '@/ui/Icons/XIcon';
import Modal from '@/ui/Modal';

import { classNames } from '@/utils/classnames';
import { fetchWithJwt } from '@/utils/fetcher';
import { triggerPaidFeedbackHint } from '@/utils/paid-feedback-hint';
import { routes } from '@/utils/routes';

interface PaidFeedbackModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onClose: () => void;
  onSuccess?: () => void;
  triggerSource: 'wallet_connect' | 'menu';
}

const USDC_MAINNET_ADDRESS = USDC_ADDRESSES[CHAIN_IDS.MAINNET];

const COMPENSATION_LABEL: Record<PaidFeedbackType, string> = {
  interview: '200 USDC',
  survey: 'Up to 50 USDC',
};

export default function PaidFeedbackModal({
  isOpen,
  onOpenChange,
  onClose,
  onSuccess,
  triggerSource,
}: PaidFeedbackModalProps) {
  const isMobile = useMedia(breakpoints.mobile, false);
  const styles = inputStyles();
  const { ethereumWallet } = useMatchaWallets();
  const titleRef = useRef<HTMLHeadingElement>(null);

  const [feedbackType, setFeedbackType] = useState<PaidFeedbackType>('interview');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  const trimmedEmail = email.trim();
  const [videoLoaded, setVideoLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handlePointerDownCapture = (e: React.PointerEvent) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'TEXTAREA' || target.tagName === 'INPUT') {
      e.stopPropagation();
      setTimeout(() => {
        target.focus();
      }, 0);
    }
  };

  const handleClose = () => {
    setEmail('');
    setError(null);
    setEmailError(null);
    onClose();

    triggerPaidFeedbackHint();
  };

  const handleSubmit = async () => {
    const walletAddress = ethereumWallet?.address;

    if (!walletAddress) {
      setError('No wallet connected');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!trimmedEmail) {
      setEmailError('Email is required');
      return;
    }
    if (!emailRegex.test(trimmedEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setEmailError(null);

    try {
      const response = await fetchWithJwt(routes.api.PAID_FEEDBACK_SUBMIT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          walletAddress,
          email: trimmedEmail,
          feedbackType,
          triggerSource,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to submit feedback signup');
      }

      ampli.paidFeedbackModalSubmitted({ wallet_address: walletAddress });

      setEmail('');
      setError(null);
      setEmailError(null);
      onClose();
      onSuccess?.();

      toast.custom((toastId) => <PaidFeedbackToast onClose={() => toast.dismiss(toastId)} />, {
        duration: Infinity,
        unstyled: true,
      });
    } catch (err) {
      logger.error('Error submitting paid feedback signup:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to submit. Please try again.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formContent = (
    <div
      className="flex flex-col h-full px-6 pt-6 pb-4"
      onPointerDownCapture={handlePointerDownCapture}
    >
      {/* Mobile close button */}
      <div className="flex justify-end mb-2 tablet:hidden">
        <Dialog.Close
          className={buttons({
            size: 'small',
            color: 'transparent',
            shape: 'rounded',
            iconOnly: true,
            class: 'p-3',
          })}
          aria-label="close paid feedback dialog"
        >
          <XIcon className="h-4 w-4 text-token-gray-1600" />
        </Dialog.Close>
      </div>

      <Dialog.Title
        ref={titleRef}
        tabIndex={-1}
        className={fontStyles({
          variant: 'H4',
          class:
            'flex items-center gap-1 flex-wrap text-token-gray-1600 mb-2 outline-none tablet:whitespace-nowrap',
        })}
      >
        <span>Earn</span>
        <TokenAvatar
          address={USDC_MAINNET_ADDRESS}
          chainId={CHAIN_IDS.MAINNET}
          size={24}
          showChainLogo={false}
          alt="USDC"
        />
        <span>USDC for your feedback</span>
      </Dialog.Title>
      <Dialog.Description
        className={fontStyles({
          variant: 'BODY_MD',
          class: 'text-token-gray-800 mb-6',
        })}
      >
        We&apos;d love to hear what you enjoy (and don&apos;t) about Matcha, what feels missing, and
        how we could make it better for you.
      </Dialog.Description>

      <div className="grid grid-cols-1 tablet:grid-cols-2 gap-4 mb-6">
        <FeedbackTypeCard
          type="interview"
          selected={feedbackType === 'interview'}
          onSelect={setFeedbackType}
          title="1:1 User Interview"
          rewardLabel={COMPENSATION_LABEL.interview}
          details={[
            { icon: <CameraOffIcon className="size-4 shrink-0" />, label: 'No camera needed' },
            { icon: <ClockIcon className="size-4 shrink-0" />, label: '45 min' },
          ]}
        />
        <FeedbackTypeCard
          type="survey"
          selected={feedbackType === 'survey'}
          onSelect={setFeedbackType}
          title="Survey"
          rewardLabel={COMPENSATION_LABEL.survey}
          details={[
            { icon: <FileTextIcon className="size-4 shrink-0" />, label: '15 questions' },
            { icon: <ClockIcon className="size-4 shrink-0" />, label: '20 min' },
          ]}
        />
      </div>

      <label
        className={fontStyles({
          variant: 'LABEL_SM',
          class: 'mb-2 block text-token-gray-1200',
        })}
      >
        Enter your email to sign up
      </label>

      <div className="flex flex-col gap-2">
        <div className={styles.root({ error: !!emailError })}>
          <input
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setEmailError(null);
            }}
            placeholder="Email"
            className={styles.input()}
          />
          {emailError && (
            <WarningCircleSolidIcon
              className={`w-4 h-4 shrink-0 ${styles.accessoryRight({ error: true })}`}
            />
          )}
        </div>
        {emailError && (
          <div className={fontStyles({ variant: 'LABEL_SM', class: 'text-token-red-900' })}>
            {emailError}
          </div>
        )}
      </div>

      <div className="flex items-start gap-2 mt-4 mb-6">
        <ShieldTickIcon className="size-4 shrink-0 mt-0.5 text-token-green-900" />
        <p className={fontStyles({ variant: 'CAPTION', class: 'text-token-gray-700' })}>
          We only store email for the purpose of scheduling the {feedbackType}. You may request
          deletion of your data anytime.
        </p>
      </div>

      <div className="hidden tablet:block tablet:flex-1" />

      {error && (
        <div className={fontStyles({ variant: 'BODY_SM', class: 'text-token-red-900 mb-2' })}>
          {error}
        </div>
      )}
      <button
        disabled={!trimmedEmail || isSubmitting}
        className={buttons({
          color: 'black',
          size: 'large',
          class: 'w-full rounded-[26px]',
        })}
        onClick={handleSubmit}
      >
        {isSubmitting ? 'Signing up...' : 'Sign up'}
      </button>
    </div>
  );

  const mediaContent = (
    <div className="relative h-full w-full bg-token-gray-200 overflow-hidden">
      {!videoLoaded && (
        <Image
          src="/images/paid-feedback/right-panel.jpg"
          alt="Earn USDC with Matcha"
          fill
          sizes="400px"
          className="object-cover"
          priority
        />
      )}
      <video
        ref={videoRef}
        className={`w-full h-full object-cover ${videoLoaded ? 'opacity-100' : 'opacity-0'}`}
        muted
        playsInline
        autoPlay
        loop
        poster="/images/paid-feedback/right-panel.jpg"
        onPlay={() => setVideoLoaded(true)}
      >
        <source src="/images/paid-feedback/video.mp4" type="video/mp4" />
      </video>
      <Dialog.Close
        className={buttons({
          size: 'small',
          color: 'transparent',
          shape: 'rounded',
          iconOnly: true,
          class:
            'absolute top-4 right-4 z-10 p-3 hover:bg-[rgba(255,255,255,0.15)] active:bg-[rgba(255,255,255,0.25)]',
        })}
        aria-label="close paid feedback dialog"
      >
        <XIcon className="h-4 w-4 text-primitive-basic-white" />
      </Dialog.Close>
    </div>
  );

  const desktopContent = (
    <div className="flex h-[540px]">
      <div className="w-1/2 border-r border-token-gray-300">{formContent}</div>
      <div className="w-1/2 hidden tablet:block">{mediaContent}</div>
    </div>
  );

  const mobileContent = <div className="flex flex-col">{formContent}</div>;

  return (
    <>
      {isMobile ? (
        <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 bg-special-background-modal z-[200]" />
            <Dialog.Content
              className="fixed inset-0 z-[200] bg-special-background-modal overflow-auto"
              onOpenAutoFocus={(e) => {
                e.preventDefault();
                titleRef.current?.focus();
              }}
              onEscapeKeyDown={handleClose}
              onPointerDownOutside={handleClose}
            >
              {mobileContent}
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      ) : (
        <Modal
          variant="XLARGE"
          isOpen={isOpen}
          onOpenChange={onOpenChange}
          onClose={handleClose}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            titleRef.current?.focus();
          }}
          className="overflow-hidden tablet:w-[800px] tablet:rounded-[24px]"
        >
          {desktopContent}
        </Modal>
      )}
    </>
  );
}

interface FeedbackTypeCardProps {
  type: PaidFeedbackType;
  selected: boolean;
  onSelect: (type: PaidFeedbackType) => void;
  title: string;
  rewardLabel: string;
  details: { icon: React.ReactNode; label: string }[];
}

function FeedbackTypeCard({
  type,
  selected,
  onSelect,
  title,
  rewardLabel,
  details,
}: FeedbackTypeCardProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={() => onSelect(type)}
      className={classNames(
        'flex flex-col gap-4 items-start p-3 text-left transition-colors tablet:h-[146px]',
        selected
          ? 'bg-special-background-modal border-2 border-token-purple-900 shadow-25 rounded-[10px]'
          : 'bg-token-gray-300 tablet:bg-token-gray-200 border-2 border-transparent hover:bg-token-gray-300 rounded-[12px]',
      )}
    >
      <div className="flex flex-col gap-2 items-start">
        <RadioDot selected={selected} />
        <div className="flex flex-col gap-1.5">
          <p
            className={fontStyles({
              variant: 'LABEL_MD',
              class: 'text-token-gray-1600',
            })}
          >
            {title}
          </p>
          <div className="flex items-center gap-[3px]">
            <TokenAvatar
              address={USDC_MAINNET_ADDRESS}
              chainId={CHAIN_IDS.MAINNET}
              size={16}
              showChainLogo={false}
              alt="USDC"
            />
            <p
              className={fontStyles({
                variant: 'BODY_SM',
                class: 'text-token-gray-900',
              })}
            >
              {rewardLabel}
            </p>
          </div>
        </div>
      </div>
      <ul className="flex flex-col gap-2.5">
        {details.map((d) => (
          <li key={d.label} className="flex items-start gap-[11px]">
            <span className="text-token-gray-800">{d.icon}</span>
            <span
              className={fontStyles({
                variant: 'CAPTION',
                class: 'text-token-gray-800',
              })}
            >
              {d.label}
            </span>
          </li>
        ))}
      </ul>
    </button>
  );
}

function RadioDot({ selected }: { selected: boolean }) {
  if (selected) {
    return (
      <span
        aria-hidden
        className="size-4 rounded-full bg-token-purple-900 shadow-15 inline-flex items-center justify-center"
      >
        <span className="size-2 rounded-full bg-primitive-basic-white" />
      </span>
    );
  }
  return (
    <span aria-hidden className="size-4 rounded-full border-2 border-token-gray-700 inline-block" />
  );
}

interface IconProps {
  className?: string;
}

function ClockIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <path
        d="M8 4.667V8l2 1.333M14 8A6 6 0 1 1 2 8a6 6 0 0 1 12 0Z"
        stroke="currentColor"
        strokeWidth="1.333"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CameraOffIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <path
        d="m1.333 1.333 13.334 13.334M14 11.467V5.667a1 1 0 0 0-1-1H9.86l-.527-.79a1 1 0 0 0-.832-.444H7.167L4.667 4.5M2 5.667v6.666a1 1 0 0 0 1 1h8m-3.5-2.5a2 2 0 1 1-2.4-2.4"
        stroke="currentColor"
        strokeWidth="1.333"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function FileTextIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <path
        d="M9.333 1.333H4a1.333 1.333 0 0 0-1.333 1.334v10.666A1.333 1.333 0 0 0 4 14.667h8a1.333 1.333 0 0 0 1.333-1.334V5.333L9.333 1.333Z"
        stroke="currentColor"
        strokeWidth="1.333"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9.333 1.333v4h4M5.333 8.667h5.334M5.333 11.333h5.334M6.667 6H5.333"
        stroke="currentColor"
        strokeWidth="1.333"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
